import { apiRequest } from '@/lib/api';
/**
 * Campaign Management Hub - Central orchestration point
 * 
 * UPDATED JAN 27, 2026: CampaignSetupWizard is now deprecated.
 * New campaigns should use the /workflow route (WorkflowPage - 12-step workflow)
 * This hub now redirects to /campaigns/create for campaign selection
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  Plus, 
  Settings, 
  Users, 
  Target,
  Zap,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
// DEPRECATED: import { CampaignSetupWizard } from './CampaignSetupWizard';
import { CampaignWorkflowDashboard } from './CampaignWorkflowDashboard';
import { AgentOrchestrationPanel } from './AgentOrchestrationPanel';

interface Campaign {
  id: string;
  name: string;
  description: string;
  framework: 'PAS' | 'AIDA' | 'BAB' | 'STAR';
  status: 'setup' | 'active' | 'paused' | 'completed';
  progress: number;
  clientId: string;
  primaryPartnerId: string;
  targetIndustries: string[];
  createdAt: Date;
  agents: {
    content: string;
    research: string;
    outreach: string;
    analytics: string;
  };
}

interface CampaignHubProps {}

export const CampaignManagementHub: React.FC<CampaignHubProps> = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'hub' | 'setup' | 'workflow' | 'orchestration'>('hub');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  // Fetch campaigns from backend API
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/v1/campaigns');
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      } else {
        console.error('Failed to fetch campaigns:', response.statusText);
        console.log('Falling back to empty campaigns list');
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      console.log('Backend connection failed, showing empty state');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // Load campaigns on component mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleCreateCampaign = async (campaignData: { name?: string; type?: string; budget?: number }) => {
    try {
      const response = await apiRequest('/api/v1/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaignData),
      });

      if (response.ok) {
        const newCampaign = await response.json();
        
        // Convert backend response to frontend Campaign format
        const formattedCampaign: Campaign = {
          id: newCampaign.id,
          name: newCampaign.name,
          description: newCampaign.description,
          framework: newCampaign.framework,
          status: newCampaign.status,
          progress: newCampaign.progress || 0,
          clientId: newCampaign.client_id,
          primaryPartnerId: newCampaign.primary_partner_id,
          targetIndustries: newCampaign.target_industries || [],
          createdAt: new Date(newCampaign.created_at),
          agents: {
            content: 'initializing',
            research: 'ready',
            outreach: 'ready',
            analytics: 'ready'
          }
        };
        
        setCampaigns(prev => [...prev, formattedCampaign]);
        setSelectedCampaign(formattedCampaign);
        setActiveView('workflow');
      } else {
        console.error('Failed to create campaign:', response.statusText);
        // Fallback: Create campaign locally if backend fails
        const fallbackCampaign: Campaign = {
          id: `campaign-${Date.now()}-backend-offline`,
          name: campaignData.name,
          description: campaignData.description,
          framework: campaignData.framework,
          status: 'active',
          progress: 0,
          clientId: campaignData.clientId || 'offline-client',
          primaryPartnerId: campaignData.primaryPartnerId || 'offline-partner',
          targetIndustries: campaignData.targetIndustries || [],
          createdAt: new Date(),
          agents: {
            content: 'initializing',
            research: 'ready',
            outreach: 'ready',
            analytics: 'ready'
          }
        };
        console.log('Backend unavailable, created local campaign:', fallbackCampaign.name);
        setCampaigns(prev => [...prev, fallbackCampaign]);
        setSelectedCampaign(fallbackCampaign);
        setActiveView('workflow');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      // Fallback: Create campaign locally if network fails
      const fallbackCampaign: Campaign = {
        id: `campaign-${Date.now()}-network-error`,
        name: campaignData.name,
        description: campaignData.description,
        framework: campaignData.framework,
        status: 'active',
        progress: 0,
        clientId: campaignData.clientId || 'offline-client',
        primaryPartnerId: campaignData.primaryPartnerId || 'offline-partner',
        targetIndustries: campaignData.targetIndustries || [],
        createdAt: new Date(),
        agents: {
          content: 'initializing',
          research: 'ready',
          outreach: 'ready',
          analytics: 'ready'
        }
      };
      console.log('Network error, created local campaign:', fallbackCampaign.name);
      setCampaigns(prev => [...prev, fallbackCampaign]);
      setSelectedCampaign(fallbackCampaign);
      setActiveView('workflow');
    }
  };

  const handleCampaignSelect = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setActiveView('workflow');
  };

  const renderCampaignCard = (campaign: Campaign) => (
    <Card key={campaign.id} className="cursor-pointer hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{campaign.name}</CardTitle>
          <div className="flex gap-2">
            {(campaign.id.includes('backend-offline') || campaign.id.includes('network-error')) && (
              <Badge variant="outline" className="text-xs">
                Local
              </Badge>
            )}
            <Badge variant={
              campaign.status === 'active' ? 'default' :
              campaign.status === 'completed' ? 'secondary' :
              campaign.status === 'paused' ? 'destructive' : 'secondary'
            }>
              {campaign.status}
            </Badge>
          </div>
        </div>
        <p className="text-gray-600 text-sm line-clamp-2">{campaign.description}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>{campaign.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                style={{ width: `${campaign.progress}%` }}
              />
            </div>
          </div>

          {/* Framework & Industries */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <Target className="h-4 w-4 text-gray-400" />
              <span className="font-medium">{campaign.framework}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-400" />
              <span>{campaign.targetIndustries.length} industries</span>
            </div>
          </div>

          {/* Agent Status */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(campaign.agents).map(([agent, status]) => (
              <div key={agent} className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  status === 'ready' ? 'bg-green-400' :
                  status === 'active' ? 'bg-blue-400' :
                  status === 'initializing' ? 'bg-yellow-400' : 'bg-gray-400'
                }`} />
                <span className="capitalize">{agent}</span>
              </div>
            ))}
          </div>

          <Button 
            variant="secondary" 
            size="medium" 
            className="w-full"
            onClick={() => handleCampaignSelect(campaign)}
          >
            Open Campaign
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // DEPRECATED: Old setup view used CampaignSetupWizard
  // Now redirects to the canonical /campaigns/create → /workflow flow
  if (activeView === 'setup') {
    // Use navigate hook from react-router
    const navigate = useNavigate();
    // Redirect to canonical campaign creation flow
    navigate('/workflow/1');
    return null;
  }

  if (activeView === 'workflow' && selectedCampaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button 
            variant="secondary" 
            onClick={() => setActiveView('hub')}
          >
            ← Back to Hub
          </Button>
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold">{selectedCampaign.name}</h2>
            <Button 
              variant="secondary"
              onClick={() => setActiveView('orchestration')}
            >
              <Zap className="h-4 w-4 mr-2" />
              Agent Control
            </Button>
          </div>
        </div>
        <CampaignWorkflowDashboard campaignId={selectedCampaign.id} />
      </div>
    );
  }

  if (activeView === 'orchestration' && selectedCampaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button 
            variant="secondary" 
            onClick={() => setActiveView('workflow')}
          >
            ← Back to Workflow
          </Button>
          <h2 className="text-xl font-semibold">Agent Orchestration - {selectedCampaign.name}</h2>
          <div></div>
        </div>
        <AgentOrchestrationPanel 
          campaignId={selectedCampaign.id} 
          currentPhase="content-orchestration"
        />
      </div>
    );
  }

  // Main Hub View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campaign Management</h1>
          <p className="text-gray-600">Orchestrate AI-powered B2B campaigns with intelligent agents</p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setActiveView('setup')}
          className="flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Start Project</span>
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Target className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Campaigns</p>
                <p className="text-2xl font-bold">{campaigns.filter(c => c.status === 'active').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold">{campaigns.filter(c => c.status === 'completed').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Zap className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Agents</p>
                <p className="text-2xl font-bold">
                  {campaigns.reduce((total, c) => 
                    total + Object.values(c.agents).filter(status => status === 'active').length, 0
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Reach</p>
                <p className="text-2xl font-bold">2.4K</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Grid */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Your Campaigns</h2>
        {loading ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="mx-auto h-12 w-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading campaigns...</h3>
              <p className="text-gray-600">Connecting to backend API to fetch your campaigns</p>
            </CardContent>
          </Card>
        ) : campaigns.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Target className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaigns yet</h3>
              <p className="text-gray-600 mb-6">Create your first AI-powered campaign to get started</p>
              <Button 
                variant="primary"
                onClick={() => setActiveView('setup')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map(renderCampaignCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignManagementHub;
